import { useState, useCallback } from 'react';
import { Settings, Home } from 'lucide-react'; // Import engine icon
import AudioUploader from './components/AudioUploader';
import MermaidDiagram from './components/MermaidDiagram';
import ResizeHandle from './components/ResizeHandle';
import { buildGraph } from './AgenticFlow';
import TextUploader from './components/TextUploader';
import { AppContext } from './AppContext';
import { ProgressModal } from './components/ProgressModal';
import TextView from './components/TextView';
import SettingsPanel from './components/SettingsPanel';
import SwitchButton from './components/SwitchButton';



function App() {
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // percentage
  const [input, setInput] = useState<string | Blob>("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [progressLabel, setProgressLabel] = useState("Processing...");
  const [activeMenu, setActiveMenu] = useState<'Main' | 'Settings'>('Main'); // State for left menu navigation
  const [mindmap, setMindmap] = useState(`graph TD`);
  const [summary, setSummary] = useState('');
  const [openAIApiKey, setOpenAIApiKey] = useState(process.env.OPENAI_API_KEY);
  const [assemblyAIApiKey, setAssemblyAIApiKey] = useState(process.env.ASSEMBLYAI_API_KEY);

  const handleResize = useCallback((newWidth: number) => {
    const constrainedWidth = Math.min(Math.max(newWidth, 20), 50); // Constrain the width
    setLeftPanelWidth(constrainedWidth);
  }, []);

  const handleProcessClick = async () => {
    try {
      setIsModalVisible(true);
      setProgressLabel("Processing request...");

      console.log("process");
      const graph = buildGraph({ openAIApiKey, assemblyAIApiKey });
      const app = graph.compile();

      const res = await app.stream(
        { input, progress: (label: string) => setProgressLabel(label) },
        { streamMode: 'values' }
      );

      for await (const chunk of res) {
        console.dir(chunk);

        if (chunk.summary) {
          setSummary(chunk.summary);
        }

        if (chunk.mindmap) {
          setMindmap(chunk.mindmap);
        }
      }
    } catch (error: unknown) {
      setProgressLabel("Error occurred. Please try again." + error);
    } finally {
      setTimeout(() => setIsModalVisible(false), 1000); // Close modal after delay
    }
  };

  return (
    <AppContext.Provider
      value={{ input, setInput, openAIApiKey, setOpenAIApiKey, assemblyAIApiKey, setAssemblyAIApiKey }}
    >
      <div className="min-h-screen flex flex-col relative">
        <ProgressModal visible={isModalVisible} progress={progressLabel} />

        <div id="main-container"className="flex">
          {/* Left Vertical Menu */}
          <div className="w-16 bg-gray-800 text-white flex flex-col items-center py-4">
            <button
              className={`mb-4 p-4 ${activeMenu === 'Main' ? 'bg-gray-600 rounded' : ''}`}
              onClick={() => setActiveMenu('Main')}
            >
              <Home size={24} />
            </button>
            <button
              className={`mb-4 p-4 ${activeMenu === 'Settings' ? 'bg-gray-600 rounded' : ''}`}
              onClick={() => setActiveMenu('Settings')}
            >
              <Settings size={24} />
            </button>
          </div>

          {/* Conditional Main Content */}
          {activeMenu === 'Main' && (
            <div id="main" className="flex-1 flex flex-col">
              <div className="flex">
                <LeftPanel leftPanelWidth={leftPanelWidth} />

                <ResizeHandle onResize={handleResize} />

                <RightPanel
                  leftPanelWidth={leftPanelWidth}
                  mindmap={mindmap}
                  summary={summary}
                />
              </div>

              <div id="process" className="flex justify-center items-center h-12 bg-gray-200" style={{ marginTop: '-16px' }}>
                <ProcessButton
                  handleProcessClick={handleProcessClick}
                  input={input}
                  openAIApiKey={openAIApiKey}
                  assemblyAIApiKey={assemblyAIApiKey}
                />
              </div>
            </div>
          )}

          {/* Settings Page */}
          {activeMenu === 'Settings' && (
            <div id="settings" className="flex flex-1 items-center justify-center min-h-screen bg-gray-100">
              <SettingsPanel />
            </div>
          )}
        </div>

        {/* Status Bar */}
        <StatusBar />
      </div>
    </AppContext.Provider>
  );
}

type ProcessButtonProps = {
  handleProcessClick: () => void;
  input: string | Blob;
  openAIApiKey: string | undefined;
  assemblyAIApiKey: string | undefined;
}

function ProcessButton({ handleProcessClick, input, openAIApiKey, assemblyAIApiKey }: ProcessButtonProps) {
  // Check if all required values are provided
  const isEnabled = input && openAIApiKey && assemblyAIApiKey;

  return (
      <button
        className={`px-4 py-2 ${isEnabled ? 'bg-blue-600' : 'bg-gray-400'} text-white rounded`}
        onClick={handleProcessClick}
        disabled={!isEnabled} // Disable button if any value is missing
      >
        Process
      </button>
    
  )
}


function LeftPanel({ leftPanelWidth }: { leftPanelWidth: number }) {
  const [activeTabL, setActiveTabL] = useState<'Audio' | 'Text'>('Audio'); // State for tabs
  const [activeSwitches, setActiveSwitches] = useState<boolean[]>([true, false, false, false]); // State for switch buttons

  const handleSwitchClick = (index: number, value: boolean) => {
    setActiveSwitches((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };
  return (
    <>
      {/* Left Panel */}
      <div
        className="flex-shrink-0 bg-gray-100"
        style={{ height: `calc(100vh - 4rem)`, width: `${leftPanelWidth}%` }}
      >
        {/* Top Bar with Switch Buttons */}
        <div className="flex justify-around items-center h-10 bg-gray-200 border-b px-2">
          <SwitchButton index={0} label="Key Points" active={activeSwitches[0]} onClick={handleSwitchClick} disabled />
          <SwitchButton index={1} label="Problem Solution" active={activeSwitches[1]} onClick={handleSwitchClick} disabled />
          <SwitchButton index={2} label="Questions & Answers" active={activeSwitches[2]} onClick={handleSwitchClick} disabled/>
          <SwitchButton index={3} label="Topics & Subtopic" active={activeSwitches[3]} onClick={handleSwitchClick} disabled/>
        </div>

        {/* Left Tabs */}
        <div className="flex border-b">
          <button
            className={`mx-6 my-1 h-10 w-1/2 ${
              activeTabL === 'Text' ? 'bg-blue-500 text-white rounded-t-xl' : 'bg-white'
            }`}
            onClick={() => setActiveTabL('Text')}
          >
            💬 Transcript
          </button>
          <button
            className={`mx-6 my-1 h-10 w-1/2 ${
              activeTabL === 'Audio' ? 'bg-blue-500 text-white rounded-t-xl' : 'bg-white'
            }`}
            onClick={() => setActiveTabL('Audio')}
          >
            🔊 Audio
          </button>
        </div>

        {/* Content */}
        {activeTabL === 'Text' && <TextUploader />}
        {activeTabL === 'Audio' && <AudioUploader />}
      </div>
    </>
  );
}

function RightPanel( { leftPanelWidth, mindmap, summary }: { leftPanelWidth: number, mindmap: string, summary: string } ) {
  const [activeTabR, setActiveTabR] = useState<'Diagram' | 'Summary'>('Diagram'); // State for tabs

  return ( 
    <>
        {/* Right Panel */}
        <div
        className="flex-1"
        style={{ height: `calc(100vh - 4rem)`, width: `${100 - leftPanelWidth}%` }}
      >
        {/* Right Tabs */}
        <div className="flex border-b">
          <button
            className={`mx-6 my-1 h-10 w-1/2 ${activeTabR === 'Diagram' ? 'bg-blue-500 text-white rounded-t-xl' : 'bg-white'}`}
            onClick={() => setActiveTabR('Diagram')}
          >
            🧠 Mind Map 🗺️
          </button>
          <button
            className={`mx-6 my-1 h-10 w-1/2 ${activeTabR === 'Summary' ? 'bg-blue-500 text-white rounded-t-xl' : 'bg-white'}`}
            onClick={() => setActiveTabR('Summary')}
          >
            💬 Summary
          </button>
        </div>
        {/* Content */}
        {activeTabR === 'Diagram' && <MermaidDiagram diagram={mindmap} />}
        {activeTabR === 'Summary' && <TextView text={summary} />}
      </div>  
  </>)
}

function StatusBar() {
  return (
    <div className="fixed bottom-0 w-full h-8 bg-gray-800 text-white flex justify-between items-center px-4 text-sm">
      <span>
        Version: {" "}
        <span className="text-yellow-400">{process.env.version ?? ''}</span>
      </span>
      <span>
        Powered by:{" "}
        <a
          href="https://github.com/bsorrentino"
          target="new"
          rel="noopener noreferrer"
          className="text-blue-400 hover:underline"
        >
          bsorrentino
        </a>
      </span>
    </div>
  );
}export default App;